/**
 * Where a parent lives: the family's money, goals and people, in five parts.
 *
 * REDESIGNED 2026-09-24. This was one screen doing seven jobs -- the device
 * alerts, the valley, the shortcuts, an always-open form for money, the
 * people, the goals and the recent list -- 3,400 pixels tall on a phone, with
 * the way to anything else (the journal, settings, sign out) at the very
 * bottom. It is now five sections behind a navigation bar that is always on
 * screen: Home, Money, Goals, Family and Settings. Each does one job.
 *
 * THIS COMPONENT STILL OWNS THE DATA for all five, which is why they are
 * sections of it rather than separate screens: the family is read once,
 * polled in one place, and every section sees the same state. Moving between
 * them re-reads nothing unless the family has changed (one revision check).
 *
 * Every figure here is DERIVED. The server sums an append-only ledger on each
 * read and stores no balance anywhere, so nothing on this screen can disagree
 * with the rows it came from.
 *
 * Names and characters arrive as ciphertext and are opened by state.ts before
 * this component sees them. Nothing in this file touches a key, and nothing in
 * it does arithmetic on money.
 */

import { Loading } from '../components/Spinner';
import { useDragScroll } from '../components/useDragScroll';
import { FamilyMark } from '../components/FamilyMark';
import { KeyRequests } from './KeyRequests';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  approveLook,
  discardLook,
  loadState,
  WrongFamilyKey,
  readRevision,
  recordMoney,
  removePerson,
  revertLook,
  saveLook,
  savePerson,
  saveSettings,
  saveUnit,
  type FamilyState,
  type Person,
} from '../family/state';
import { amountInputMode, amountPlaceholder, asTyped, bigAmount, currentUnit, dollars, howMuch, inLabel, looksLikeAmount, normaliseAmount, shortAmount, signed, unitNoun } from '../family/money';
import { Character } from '../avatar/Character';
import { Builder } from '../avatar/Builder';
import { ChildCode } from './ChildCode';
import { Allowance } from './Allowance';
import { currentAccount } from '../auth/cognito';
import { hasPin } from '../auth/pin';
import { recoverySaved } from './RecoverySheet';
import { RememberedJar } from '../valley/MarbleJar';
import { inList, localToday, readableDate } from '../family/goals';
import { Goals } from './Goals';
import { QuickActions } from './QuickActions';
import { Journal } from './Journal';
import { Settings } from './Settings';
import { CountUp } from '../brand/CountUp';
import { GoalPicture, MoreMenu } from '../brand/GoalPicture';
import { Layer } from '../brand/Layer';
import { SETTINGS_ICONS } from '../brand/settingsIcons';
import { stage, STAGE_LABELS } from '../valley/scene';
import { useReportBackdrop } from '../brand/backdrop';
import { ParentNav } from './ParentNav';
import { CloseButton } from '../components/CloseButton';
import { useDismissed } from '../family/dismissed';
import { ConsentSheet } from './ConsentSheet';
import { ConsentLanding } from './ConsentLanding';
import { needsPermission } from '../family/consent';
import { entryParts, splitLine } from '../family/split';

/** Only while somebody is waiting. Home is idle the rest of the time. */
const PENDING_POLL_MS = 10_000;

export type Tab = 'home' | 'money' | 'goals' | 'family' | 'settings';

export function Home({
  familyId,
  familyKey,
  tab,
  onTab,
  settingsFocus,
  onChildPage,
  onFullValley,
  onSignOut,
  onForgetDevice,
  onRenamed,
  onSwitchAccount,
}: {
  familyId: string;
  familyKey: CryptoKey;
  tab: Tab;
  onTab: (tab: Tab, focus?: string) => void;
  /** A section of Settings to open at, e.g. 'people-devices'. */
  settingsFocus: string | null;
  /** Open this person's own page as they see it. Read-only; see ChildPage. */
  onChildPage: (state: FamilyState, personId: string) => void;
  /** Hand the whole screen over to the valley. See FullValley. */
  onFullValley: (state: FamilyState) => void;
  onSignOut: () => void;
  onForgetDevice: () => void;
  onRenamed: (name: string) => void;
  /** Lock this account and show the account picker. Lives in Settings. */
  onSwitchAccount?: () => void;
}) {
  const [state, setState] = useState<FamilyState | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Whose character is being edited, if anybody's. */
  const [styling, setStyling] = useState<Person | null>(null);
  // The painting behind the whole parent app follows the family's money.
  useReportBackdrop(state?.balance, state?.gardenTarget);

  const refresh = useCallback(async () => {
    try {
      setState(await loadState(familyId, familyKey));
    } catch (problem) {
      // Another parent started the family over while this screen was open.
      // The old key is already forgotten; a reload lands on the code screen.
      if (problem instanceof WrongFamilyKey) window.location.reload();
      throw problem;
    }
  }, [familyId, familyKey]);

  useEffect(() => {
    void refresh().catch((problem: unknown) => {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    });
  }, [refresh]);

  /**
   * A custom unit's name is sealed for each child who existed when it was
   * saved. Somebody added since cannot read what they count in on their own
   * device, so it is sealed again, for everybody, the first time this device
   * notices. The same quiet repair `savePerson` does for keys.
   */
  useEffect(() => {
    if (!state || state.unit.type !== 'custom') return;
    const missing = state.people.some(
      (p) => p.key && p.status === 'approved' && !state.unitWrappedFor.includes(p.id),
    );
    if (!missing) return;
    void saveUnit(familyId, familyKey, state.unit, state.people)
      .then(() => refresh())
      .catch(() => {
        // Not worth interrupting anybody over; the next load tries again.
      });
  }, [state, familyId, familyKey, refresh]);

  /**
   * The family's name is sealed for each child too, so a child's own device
   * can say which family it is showing (a child may belong to two). Anybody
   * added since it was last sealed -- and every family from before this
   * existed -- is sealed in here, quietly, like the unit's name above.
   */
  useEffect(() => {
    if (!state || !state.familyName) return;
    const missing =
      !state.kidNameFor.includes('fam') ||
      state.people.some((p) => p.key && p.status === 'approved' && !state.kidNameFor.includes(p.id));
    if (!missing) return;
    void saveSettings(familyId, familyKey, { name: state.familyName }, state.people)
      .then(() => refresh())
      .catch(() => {
        // The next load tries again.
      });
  }, [state, familyId, familyKey, refresh]);

  /**
   * Poll, but ONLY while a device is waiting to be approved.
   *
   * Home does not poll otherwise, deliberately: a parent's own screen is not
   * a wall display and re-reading the family every ten seconds is money spent
   * to learn nothing. A child standing there waiting is the one case where
   * the screen has to find something out on its own. It polls the REVISION
   * route -- one GetItem -- and reads the family only when that moves.
   */
  // A parent's own locked device asking counts as somebody waiting too.
  const waiting = (state?.pendingDevices ?? 0) + (state?.pendingKeyRequests ?? 0);
  useEffect(() => {
    if (!waiting) return;
    let stop = false;
    const timer = window.setInterval(() => {
      void readRevision(familyId)
        .then((now) => {
          if (!stop && now !== state?.revision) void refresh().catch(() => {});
        })
        .catch(() => {
          // A refused or dropped poll is not news. The next one is in ten
          // seconds, and the banner is still on screen meanwhile.
        });
    }, PENDING_POLL_MS);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [waiting, state?.revision, familyId, refresh]);

  /**
   * One cheap look for news on every change of section. A child who sends a
   * new character from their tablet should be waiting on the Family section
   * when a parent opens it, without Home polling all day to catch it.
   */
  useEffect(() => {
    if (!state) return;
    let stop = false;
    void readRevision(familyId)
      .then((now) => {
        if (!stop && now !== state.revision) void refresh().catch(() => {});
      })
      .catch(() => {});
    return () => {
      stop = true;
    };
    // Only the section changing should trigger this, not every new state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function attempt(work: () => Promise<void>): Promise<boolean> {
    setError('');
    setBusy(true);
    try {
      await work();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
      setBusy(false);
      return false;
    }
    try {
      await refresh();
    } catch {
      // The write succeeded. Do not encourage a duplicate retry just because
      // the follow-up read failed.
      setError('Saved, but the latest changes could not load. Reload this page to see them.');
    } finally {
      setBusy(false);
    }
    return true;
  }

  // Reminders a parent has closed. Owned here so the Home badge and the
  // "Needs you" list agree: a closed reminder is gone from both (2026-10-13).
  const closed = useDismissed(familyId);
  const stillWaiting = state
    ? needTitles(state, state.people.filter((person) => person.status !== 'removed'), familyId).filter(
        (need) => !closed.isDismissed(needSignature(need)),
      ).length
    : 0;

  const nav = (
    <ParentNav
      tab={tab}
      onTab={(next) => {
        setStyling(null);
        onTab(next);
      }}
      badges={{
        // ONE count, on Home, of the things in "Needs you" -- not a dot on
        // every tab, which read as noise. The sections show their own.
        home: stillWaiting,
      }}
    />
  );

  if (!state) {
    return (
      <>
        {error ? (
          <section className="section">
            <p className="error">{error}</p>
          </section>
        ) : (
          <Loading fill label="Reading your family’s valley…" />
        )}
        {nav}
      </>
    );
  }

  const roster = state.people.filter((person) => person.status !== 'removed');
  const record = (entry: { amount: string; reason: string; people: string[] }) =>
    attempt(() => recordMoney(familyId, familyKey, entry, roster));

  // Editing somebody's character: the display's builder, over everything.
  // A parent is who approves looks, so theirs is published straight away.
  if (styling) {
    return (
      <Builder
        initial={styling.avatar}
        name={styling.name}
        saveLabel="Save this look"
        busy={busy}
        error={error}
        backLabel="Back to the family"
        note="It changes straight away, on every screen in the family."
        onSave={(spec, name) =>
          void attempt(() => saveLook(familyId, familyKey, styling, spec, name)).then((saved) => {
            if (saved) setStyling(null);
          })
        }
        onCancel={() => setStyling(null)}
      />
    );
  }

  return (
    <>
      {/* The nav comes FIRST in the page, so Tab reaches it before the
          content -- on a wide screen it is drawn at the top, and it was the
          last thing a keyboard got to. CSS places it either way. */}
      {nav}
      {error ? <p className="error">{error}</p> : null}

      {tab === 'home' ? (
        <Overview
          state={state}
          people={roster}
          familyId={familyId}
          familyKey={familyKey}
          busy={busy}
          onTab={onTab}
          onRecord={record}
          onChanged={(work) => void attempt(work)}
          onFullValley={() => onFullValley(state)}
          onOpenPerson={(personId) => onChildPage(state, personId)}
          closed={closed}
        />
      ) : null}

      {tab !== 'home' ? <PageBand tab={tab} state={state} people={roster} /> : null}

      {tab === 'money' ? (
        <div className="page page-money">
          <section className="section money-record">
            <h2 className="visually-hidden">Add or spend</h2>
            <Record people={roster} busy={busy} onRecord={record} />
          </section>
          {/* One column on a wide screen, scrolling on its own while the
              add-or-spend panel beside it stays put (living-valley.css 36). */}
          <div className="money-side">
          <section className="section" id="allowance">
            <Allowance
              familyId={familyId}
              people={roster}
              plans={state.allowance}
              busy={busy}
              onChanged={(work) => void attempt(work)}
            />
          </section>
          <section className="section">
            <Journal
              familyId={familyId}
              familyKey={familyKey}
              people={roster}
              familyName={state.familyName}
              goals={state.goals}
              // Re-mounting on the family's revision is what makes an entry
              // just added above appear in the list below it.
              key={state.revision}
            />
          </section>
          </div>
        </div>
      ) : null}

      {tab === 'goals' ? (
        <section className="section page page-goals">
          <Goals
            familyId={familyId}
            familyKey={familyKey}
            goals={state.goals}
            people={roster}
            free={state.free}
            splits={state.splits}
            busy={busy}
            focus={settingsFocus}
            onChanged={(work) => void attempt(work)}
          />
        </section>
      ) : null}

      {tab === 'family' ? (
        <Family
          people={roster}
          busy={busy}
          onOpen={(person) => onChildPage(state, person.id)}
          onStyle={setStyling}
          onApprove={(person) => void attempt(() => approveLook(familyId, person.id))}
          onDecline={(person) => void attempt(() => discardLook(familyId, person.id))}
          onRevert={(person) => void attempt(() => revertLook(familyId, person.id))}
          onRemove={(person) => void attempt(() => removePerson(familyId, person.id))}
          onAdd={(name) => attempt(() => savePerson(familyId, familyKey, name))}
          onDevices={() => onTab('settings', 'people-devices')}
          renderCode={(person) => (
            <ChildCode familyId={familyId} familyKey={familyKey} person={person} />
          )}
        />
      ) : null}

      {tab === 'settings' ? (
        <section className="section page page-settings">
          <Settings
            familyId={familyId}
            familyKey={familyKey}
            familyName={state.familyName}
            people={roster}
            focus={settingsFocus}
            onRenamed={(name) => {
              onRenamed(name);
              void refresh().catch(() => {});
            }}
            onRestored={() => void refresh().catch(() => {})}
            onUnitChanged={() => void refresh().catch(() => {})}
            onSignOut={onSignOut}
            onForgetDevice={onForgetDevice}
            {...(onSwitchAccount ? { onSwitchAccount } : {})}
          />
        </section>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Home: the valley, what needs a parent, and the last few things that happened
// ---------------------------------------------------------------------------

function Overview({
  state,
  people,
  familyId,
  familyKey,
  busy,
  onTab,
  onRecord,
  onChanged,
  onFullValley,
  onOpenPerson,
  closed,
}: {
  state: FamilyState;
  people: Person[];
  familyId: string;
  familyKey: CryptoKey;
  busy: boolean;
  onTab: (tab: Tab, focus?: string) => void;
  onRecord: (entry: { amount: string; reason: string; people: string[] }) => Promise<boolean>;
  onChanged: (work: () => Promise<void>) => void;
  onFullValley: () => void;
  onOpenPerson: (personId: string) => void;
  /** Reminders the parent has closed; shared with the Home badge. */
  closed: ReturnType<typeof useDismissed>;
}) {
  const { isDismissed, dismiss } = closed;
  const [consentFor, setConsentFor] = useState<Person | null>(null);
  const titles = Object.fromEntries(needTitles(state, people, familyId).map((need) => [need.key, need.title]));
  const looks = people.filter((person) => person.pending);
  // Goals waiting on a parent's answer. See family/goals.ts.
  const today = localToday();
  const ready = inList(state.goals, 'ready', today);
  const late = inList(state.goals, 'late', today);

  const unit = currentUnit();
  const needs: NeedsItem[] = [];
  // Parental permission first: until it is given, that child cannot use
  // their page at all (family/consent.ts).
  const unconsented = waitingForPermission(state, people);
  if (unconsented.length) {
    const first = unconsented[0]!;
    needs.push({
      key: 'consent',
      lead: <FaceRing spec={first.avatar} ring="sky" />,
      title: titles.consent!,
      body: state.privacy[first.id]?.state === 'renewal-required'
        ? 'Our privacy notice changed. Read it and confirm.'
        : state.privacy[first.id]?.state === 'pending'
          ? 'We emailed you a link. Open it to finish, or send it again.'
          : state.consentRequired
            ? 'Read what we collect, then confirm by email.'
            : 'Read what we collect from children, then confirm by email. It will soon be needed for their page.',
      action: 'Give permission',
      tone: 'dark',
      onAction: () => setConsentFor(first),
    });
  }
  if (state.pendingDevices > 0) {
    needs.push({
      key: 'devices',
      lead: <IconTile glyph="phone" tone="sky" />,
      title: titles.devices!,
      body: 'Nothing can read anything until you say yes. Check the picture matches.',
      action: 'Review',
      tone: 'dark',
      onAction: () => onTab('settings', 'people-devices'),
    });
  }
  if (looks.length) {
    needs.push({
      key: 'looks',
      lead: <FaceRing spec={looks[0]!.pending!.avatar} ring="sun" />,
      title: titles.looks!,
      body: 'Their old look stays until you say yes.',
      action: 'Have a look',
      tone: 'dark',
      onAction: () => onTab('family'),
    });
  }
  if (ready.length) {
    needs.push({
      key: 'ready',
      lead: <GoalPicture icon={ready[0]!.icon} size={52} className="ready" />,
      title: titles.ready!,
      body:
        ready.length === 1
          ? `All saved. Completing it spends ${dollars(ready[0]!.target)}.`
          : 'Each one is all saved up. Complete them one by one.',
      action: ready.length === 1 ? 'Complete it' : 'See them',
      tone: 'meadow',
      onAction: () => onTab('goals', ready.length === 1 ? `ready:${ready[0]!.id}` : 'ready'),
    });
  }
  if (late.length) {
    needs.push({
      key: 'late',
      lead: <span className="count-tile" aria-hidden="true">{late.length}</span>,
      title: titles.late!,
      body:
        late.length === 1
          ? `Was ${readableDate(late[0]!.due!)}. Missed, or more time?`
          : 'Missed, or more time?',
      action: late.length === 1 ? 'Decide' : 'Review them',
      tone: 'ghost',
      onAction: () => onTab('goals', late.length === 1 ? `late:${late[0]!.id}` : 'late'),
    });
  }
  if (!recoverySaved(familyId)) {
    needs.push({
      key: 'recovery',
      lead: <IconTile glyph="sheet" tone="debit" />,
      title: titles.recovery!,
      body: 'If you lose your family code and every device, only this gets you back in.',
      action: 'Print it',
      tone: 'ghost',
      onAction: () => onTab('settings', 'backup'),
    });
  }

  // Closed by the parent (2026-10-13). Keyed by the words, so a changed
  // reminder -- a second device asking -- comes back on its own.
  const signature = (item: NeedsItem) => needSignature(item);
  const showing = needs.filter((item) => !isDismissed(signature(item)));

  return (
    <div className="home">
      {consentFor ? (
        <ConsentSheet
          familyId={familyId}
          personId={consentFor.id}
          name={consentFor.name}
          renewal={state.privacy[consentFor.id]?.state === 'renewal-required'}
          onClose={() => setConsentFor(null)}
          onSent={() => onChanged(async () => {})}
        />
      ) : null}
      <ConsentLanding familyId={familyId} people={people} onChanged={onChanged} />
      {/* THE FUND, over the painting. The one number that matters, big
          enough to read across a kitchen, on a frosted-dark panel so it is
          AA on bright sky. Living Valley D1/D2. */}
      <section className="home-hero" aria-label={state.familyName || 'Your family'}>
        <div className="hero-panel">
          <p className="hero-eyebrow">
            {unit.type === 'marbles' ? 'In the jar' : 'In the fund'}
            <span className="hero-stage"> &middot; {STAGE_SHORT[stage(state.balance, state.gardenTarget)]}</span>
          </p>
          <div className="hero-figure-row">
            {unit.type === 'marbles' ? (
              <RememberedJar id={`family-${familyId}`} hundredths={state.balance} size={64} />
            ) : null}
            {/* The number big, and the unit's word (marbles, stars) small
                beside it: "33 marbles" at 84px ran off a phone. */}
            <p className="hero-balance balance-figure">
              <CountUp id="family-balance" value={state.balance} format={(n) => bigAmount(n).number} />
              {bigAmount(state.balance).word ? <span className="hero-unit"> {bigAmount(state.balance).word}</span> : null}
            </p>
          </div>
          <div className="hero-actions">
            <button type="button" className="sun hero-add" onClick={() => onTab('money')}>
              Add or spend {unitNoun()}
            </button>
            <span className="pill pill-in">
              +<CountUp id="family-earned" value={state.earned} format={dollars} />&nbsp;in {monthWord(state.today)}
            </span>
            <span className="pill pill-out">
              &minus;<CountUp id="family-spent" value={state.spent} format={dollars} />&nbsp;out
            </span>
          </div>
        </div>
        <ValleyPeople people={people} onOpen={onOpenPerson} />
      </section>

      <div className="home-sheet">
        <span className="sheet-handle" aria-hidden="true" />
        <button type="button" className="primary home-add" onClick={() => onTab('money')}>
          <span className="plus-coin" aria-hidden="true">+</span>
          Add or spend {unitNoun()}
        </button>

        {/* A parent's own other device, waiting to be let in. Before anything
            else, because somebody is standing at it. */}
        <KeyRequests
          familyId={familyId}
          familyKey={familyKey}
          count={state.pendingKeyRequests}
          onAnswered={() => onChanged(async () => {})}
        />

        {/* WHAT NEEDS A PARENT, as one list instead of five shouting cards.
            Each row: who or what, a line, and the one thing to do. */}
        {showing.length ? (
          <section className="needs-you" aria-labelledby="needs-you-title">
            <div className="section-head">
              <h2 id="needs-you-title">Needs you</h2>
              <span className="needs-count">
                {showing.length} {showing.length === 1 ? 'thing' : 'things'}
              </span>
            </div>
            <ul className="needs-list" aria-live="polite">
              {showing.map((item) => (
                <li key={item.key} className="needs-row">
                  <span className="needs-lead">{item.lead}</span>
                  <div className="needs-text">
                    <p className="needs-title">{item.title}</p>
                    <p className="needs-body">{item.body}</p>
                  </div>
                  <button type="button" className={`needs-action ${item.tone}`} onClick={item.onAction}>
                    {item.action}
                  </button>
                  <CloseButton label={`Hide: ${item.title}`} onClick={() => dismiss(signature(item))} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="home-block">
          <h2>Shortcuts</h2>
          <QuickActions
            familyId={familyId}
            familyKey={familyKey}
            actions={state.quickActions}
            people={people}
            busy={busy}
            onUse={(entry) => void onRecord(entry)}
            onChanged={onChanged}
          />
        </section>

        <section className="home-block">
          <div className="section-head">
            <h2>Recent</h2>
            <button type="button" className="text-button link" onClick={() => onTab('money')}>
              See everything &rarr;
            </button>
          </div>
          {state.entries.length === 0 ? (
            <p className="quiet">Nothing yet. Money you add or spend shows up here.</p>
          ) : (
            <ul className="entries">
              {state.entries.slice(0, 5).map((entry) => {
                const who = entry.people.map((id) => people.find((p) => p.id === id)).filter(Boolean) as Person[];
                return (
                  <li className={entry.voided ? 'entry voided' : 'entry'} key={entry.id}>
                    <span className="entry-face" aria-hidden="true">
                      {who.length ? <Character spec={who[0]!.avatar} size={36} /> : <FamilyMark size={36} />}
                    </span>
                    <div className="entry-body">
                      <p className="entry-reason">{entry.reason || 'No reason recorded'}</p>
                      {(() => {
                        const line = splitLine(
                          entryParts(entry, (id) => people.find((p) => p.id === id)?.name ?? ''),
                          (id) => state.goals.find((goal) => goal.id === id)?.title ?? '',
                        );
                        return line ? <p className="entry-split">{line}</p> : null;
                      })()}
                      <p className="entry-date">{friendlyDate(entry.date, state.today)}</p>
                    </div>
                    <p className={entry.amount < 0 ? 'entry-amount out' : 'entry-amount in'}>
                      {entry.amount > 0 ? '+' : ''}
                      {dollars(entry.amount)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <ShowTheValley onShow={onFullValley} onSetPin={() => onTab('settings', 'quick-access')} />
      </div>
    </div>
  );
}

/**
 * Everyone in the valley, one tap from their own page (2026-10-13). On a wide
 * screen they stand, breathing and blinking, in the space the painting left
 * under the fund; on a phone their faces sit in a row under the fund panel.
 */
function ValleyPeople({ people, onOpen }: { people: Person[]; onOpen: (personId: string) => void }) {
  const wide = useMedia('(min-width: 1024px)');
  const row = useRef<HTMLDivElement>(null);
  useDragScroll(row);
  const shown = people.filter((person) => person.status === 'approved');

  // A wide screen gives the family the whole space under the fund panel:
  // standing, as large as the space allows, in pages of two rows of five --
  // or one row of five when the window is too short for two. Measured,
  // because the space is whatever the window leaves (2026-10-13).
  const [rows, setRows] = useState(2);
  const [figure, setFigure] = useState(170);
  useEffect(() => {
    const el = row.current;
    if (!el || !wide) return;
    const measure = () => {
      const name = 34;
      const gap = 18;
      const height = el.clientHeight;
      const two = (height - gap) / 2 - name;
      const fits = two >= 130 ? 2 : 1;
      setRows(fits);
      setFigure(Math.round(Math.max(96, Math.min(280, fits === 2 ? two : height - name))));
    };
    measure();
    const watch = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    watch?.observe(el);
    return () => watch?.disconnect();
  }, [wide, shown.length > 0]);

  if (shown.length === 0) return null;

  const button = (person: Person) => (
    <li key={person.id}>
      <button
        type="button"
        className="home-person"
        onClick={() => onOpen(person.id)}
        aria-label={`${person.name || 'Somebody'}: open their page`}
      >
        <span className="home-person-art" aria-hidden="true">
          {wide ? (
            <Character spec={{ ...person.avatar, outfit: 0 }} mode="full" activity="standing" size={160} />
          ) : (
            <Character spec={person.avatar} size={52} />
          )}
        </span>
        <span className="home-person-name">{person.name || 'Somebody'}</span>
      </button>
    </li>
  );

  // Pages of five on a phone (one row), of five or ten on a wide screen;
  // swipe, or drag with a mouse, from one page to the next.
  const per = wide ? rows * 5 : 5;
  const pages: Person[][] = [];
  for (let i = 0; i < shown.length; i += per) pages.push(shown.slice(i, i + per));
  const scrolls = pages.length > 1;

  return (
    <div
      ref={row}
      className={[wide ? 'home-people standing' : 'home-people', scrolls ? 'scrolls' : ''].filter(Boolean).join(' ')}
      style={wide ? ({ ['--figure' as string]: `${figure}px` } as React.CSSProperties) : undefined}
      role="group"
      aria-label="In the valley"
      {...(scrolls ? { tabIndex: 0 } : {})}
    >
      {pages.map((page, index) => (
        <ul key={index} className="home-people-page">
          {page.map(button)}
        </ul>
      ))}
    </div>
  );
}

function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const change = () => setMatches(list.matches);
    change();
    list.addEventListener?.('change', change);
    return () => list.removeEventListener?.('change', change);
  }, [query]);
  return matches;
}

/**
 * What "Needs you" holds, as keys and the words it shows. One function, so
 * the Home badge in the bar and the list itself always count the same things
 * -- including leaving out the ones a parent has closed.
 */
function needTitles(state: FamilyState, people: Person[], familyId: string): Array<{ key: string; title: string }> {
  const today = localToday();
  const looks = people.filter((person) => person.pending);
  const ready = inList(state.goals, 'ready', today);
  const late = inList(state.goals, 'late', today);
  const out: Array<{ key: string; title: string }> = [];
  const unconsented = waitingForPermission(state, people);
  if (unconsented.length) {
    out.push({
      key: 'consent',
      title:
        unconsented.length === 1
          ? `${unconsented[0]!.name || 'Your child'} needs your permission`
          : `${unconsented.length} children need your permission`,
    });
  }
  if (state.pendingDevices > 0) {
    out.push({
      key: 'devices',
      title:
        state.pendingDevices === 1
          ? 'A device is asking to be somebody’s'
          : `${state.pendingDevices} devices are asking to be somebody’s`,
    });
  }
  if (looks.length) {
    out.push({
      key: 'looks',
      title:
        looks.length === 1
          ? `${looks[0]!.name || 'Somebody'} made a new character`
          : `${looks.length} new characters are waiting`,
    });
  }
  if (ready.length) {
    out.push({
      key: 'ready',
      title:
        ready.length === 1
          ? `${ready[0]!.title || 'A goal'} is ready to complete`
          : `${ready.length} goals are ready to complete`,
    });
  }
  if (late.length) {
    out.push({
      key: 'late',
      title:
        late.length === 1
          ? `${late[0]!.title || 'A goal'} is past its date`
          : `${late.length} goals are past their date`,
    });
  }
  if (!recoverySaved(familyId)) out.push({ key: 'recovery', title: 'Save your family’s recovery sheet' });
  return out;
}

/** Children who cannot use Birbly until a parent gives permission. */
function waitingForPermission(state: FamilyState, people: Person[]): Person[] {
  // Asked whether or not the server enforces it yet (ConsentRequired): the
  // switch-on is only safe once every family has had the chance to answer.
  return people.filter((person) => person.status !== 'removed' && needsPermission(true, state.privacy[person.id]));
}

/** How a closed reminder is remembered: by what it says. */
function needSignature(need: { key: string; title: string }): string {
  return `needs:${need.key}:${need.title}`;
}

interface NeedsItem {
  key: string;
  lead: React.ReactNode;
  title: string;
  body: string;
  action: string;
  tone: 'dark' | 'meadow' | 'ghost';
  onAction: () => void;
}

// The short stage words are content: content/valley.toml, `label`.
const STAGE_SHORT = STAGE_LABELS;

/** "Oct" for the month the server says it is. */
function monthWord(today: string): string {
  return new Date(`${today}T12:00:00`).toLocaleDateString(undefined, { month: 'short' });
}

/** A face in a coloured ring: sun = needs you, sky = a device, meadow = chosen. */
function FaceRing({ spec, ring, size = 52 }: { spec: Person['avatar']; ring: 'sun' | 'sky' | 'meadow'; size?: number }) {
  return (
    <span className={`face face-ring-${ring}`} style={{ width: size, height: size }} aria-hidden="true">
      <Character spec={spec} size={size} />
    </span>
  );
}

/** A Settings-style glyph in a coloured tile. */
function IconTile({ glyph, tone }: { glyph: keyof typeof SETTINGS_ICONS; tone: 'meadow' | 'sky' | 'sun' | 'debit' | 'ink' }) {
  return (
    <span className={`icon-tile tile-${tone}`} aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        dangerouslySetInnerHTML={{ __html: SETTINGS_ICONS[glyph] }}
      />
    </span>
  );
}

/**
 * The title of a section, on a frosted-dark panel over a band of the valley
 * (Living Valley: sub-pages keep a strip of the painting instead of the whole
 * screen). The line under it is the one fact a parent came to check.
 */
function PageBand({ tab, state, people }: { tab: Tab; state: FamilyState; people: Person[] }) {
  const active = state.goals.filter((goal) => goal.status === 'active');
  const saved = active.reduce((sum, goal) => sum + goal.pot, 0);
  const kids = people.filter((p) => p.status === 'approved' || p.status === 'pending');
  const words: Record<Exclude<Tab, 'home'>, [string, string]> = {
    money: ['Money', `${dollars(state.balance)} in the fund`],
    goals: [
      'Goals',
      active.length
        ? `${dollars(saved)} saved across ${active.length} ${active.length === 1 ? 'goal' : 'goals'}`
        : 'Nothing being saved for yet',
    ],
    family: ['Family', kids.length === 1 ? '1 person' : `${kids.length} people`],
    settings: ['Settings', state.familyName || 'Your family'],
  };
  const [title, line] = words[tab as Exclude<Tab, 'home'>];
  return (
    <header className="page-band">
      <div className="page-band-panel">
        <h2 className="page-title">{title}</h2>
        <p className="page-line">{line}</p>
      </div>
    </header>
  );
}

/** "Today", "Yesterday", or the date -- a list read at a glance. */
function friendlyDate(date: string, today: string): string {
  if (date === today) return 'Today';
  const d = new Date(`${today}T12:00:00`);
  d.setDate(d.getDate() - 1);
  if (date === d.toISOString().slice(0, 10)) return 'Yesterday';
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

// ---------------------------------------------------------------------------
// Family: everyone, their characters, and what they have asked for
// ---------------------------------------------------------------------------

function Family({
  people,
  busy,
  onOpen,
  onStyle,
  onApprove,
  onDecline,
  onRevert,
  onRemove,
  onAdd,
  onDevices,
  renderCode,
}: {
  people: Person[];
  busy: boolean;
  onOpen: (person: Person) => void;
  onStyle: (person: Person) => void;
  onApprove: (person: Person) => void;
  onDecline: (person: Person) => void;
  onRevert: (person: Person) => void;
  onRemove: (person: Person) => void;
  onAdd: (name: string) => Promise<boolean>;
  onDevices: () => void;
  /** Their code, QR and link: see ChildCode. */
  renderCode: (person: Person) => React.ReactNode;
}) {
  return (
    <div className="page page-family">
      <section className="section">
        <h2>Your family</h2>
        {people.length === 0 ? (
          <p className="quiet">
            Nobody yet. Add the people this fund is for &mdash; their names
            are encrypted on this device before they are stored.
          </p>
        ) : (
          <ul className="people">
            {people.map((person) => (
              <PersonCard
                key={person.id}
                person={person}
                busy={busy}
                onOpen={() => onOpen(person)}
                onStyle={() => onStyle(person)}
                onApprove={() => onApprove(person)}
                onDecline={() => onDecline(person)}
                onRevert={() => onRevert(person)}
                onRemove={() => onRemove(person)}
                code={renderCode(person)}
              />
            ))}
          </ul>
        )}
        <AddPerson busy={busy} onAdd={onAdd} />
      </section>

      <section className="section">
        <h2>Devices and codes</h2>
        <p className="quiet">
          The tablets and phones linked to each child, and the codes that link
          new ones.
        </p>
        <button type="button" className="full" onClick={onDevices}>
          Manage devices
        </button>
      </section>
    </div>
  );
}

function PersonCard({
  person,
  busy,
  onOpen,
  onStyle,
  onApprove,
  onDecline,
  onRevert,
  onRemove,
  code,
}: {
  person: Person;
  busy: boolean;
  onOpen: () => void;
  onStyle: () => void;
  onApprove: () => void;
  onDecline: () => void;
  onRevert: () => void;
  onRemove: () => void;
  /** Their code, shown when asked for. Only rendered while open. */
  code: React.ReactNode;
}) {
  // THE CODE IS ONE TAP AWAY, because a child who has lost theirs is
  // standing next to the parent waiting. See ChildCode.
  const [showingCode, setShowingCode] = useState(false);
  // REMOVING SOMEBODY ASKS FIRST. It used to be a one-tap link beside every
  // name on Home, and it deletes their name, their character and every
  // device linked to them. Their money stays in the ledger, which is said
  // here too, because "will this lose the money" is the first question.
  const [removing, setRemoving] = useState(false);
  const name = person.name || 'Unreadable on this device';

  const negative = person.held < 0;

  return (
    <li className={person.pending ? 'person-card has-look' : 'person-card'}>
      {/* A NEW LOOK leads the card, ringed in sun: it is the thing waiting.
          Old face, arrow, the new one bigger -- then yes or not now. */}
      {person.pending ? (
        <div className="look-request">
          <p className="look-request-title">{person.name || 'They'} designed a new look</p>
          <div className="look-compare" aria-hidden="true">
            <span className="face">
              <Character spec={person.avatar} size={72} />
            </span>
            <span className="look-arrow">&rarr;</span>
            <span className="face face-ring-sun big">
              <Character spec={person.pending.avatar} size={96} />
            </span>
          </div>
          {person.pending.renamed ? (
            <p className="look-request-note">
              It also changes their name to <strong>{person.pending.name}</strong>.
            </p>
          ) : null}
          <div className="actions">
            <button type="button" className="primary" disabled={busy} onClick={onApprove}>
              Use it
            </button>
            <button type="button" disabled={busy} onClick={onDecline}>
              Not now
            </button>
          </div>
        </div>
      ) : null}

      <div className="person-card-main">
        <span className="face">
          <Character spec={person.avatar} size={64} {...(person.name ? { title: person.name } : {})} />
        </span>
        <div className="person-card-text">
          <p className="person-card-name">{name}</p>
          {person.shared ? <p className="person-card-sub">{dollars(person.shared)} shared</p> : null}
        </div>
        <div className={negative ? 'person-card-money debit' : 'person-card-money'}>
          <strong>{dollars(person.held)}</strong>
          <span>of their own</span>
        </div>
        <MoreMenu label={`More for ${name}`}>
          <button type="button" onClick={onOpen}>
            Their page
          </button>
          <button type="button" onClick={onStyle}>
            {person.avatar ? 'Change look' : 'Make a character'}
          </button>
          <button type="button" aria-expanded={showingCode} onClick={() => setShowingCode(!showingCode)}>
            {showingCode ? 'Hide code' : 'Code & link'}
          </button>
          {person.canRevert ? (
            <button type="button" disabled={busy} onClick={onRevert}>
              Undo last look
            </button>
          ) : null}
          <button type="button" className="risky" onClick={() => setRemoving(true)}>
            Remove&hellip;
          </button>
        </MoreMenu>
      </div>

      {showingCode ? code : null}

      {/* REMOVING asks in a dialog -- the one place this app uses one, for
          the one thing that cannot be undone. The safe answer is the same
          size as the risky one. */}
      {removing ? (
        <Layer onClose={() => setRemoving(false)} busy={busy}>
        <div className="dialog-layer" onClick={(event) => event.target === event.currentTarget && setRemoving(false)}>
          <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby={`remove-${person.id}`}>
            <span className="face big">
              <Character spec={person.avatar} size={96} />
            </span>
            <h3 id={`remove-${person.id}`}>Remove {person.name || 'this person'}?</h3>
            <p>
              Their name, their character and every device linked to them go.
              Their money stays in the family&rsquo;s history and the total does
              not change.
            </p>
            {person.held !== 0 ? (
              <p className="dialog-fact">
                {negative
                  ? `${person.name || 'They'} ${person.name ? 'is' : 'are'} ${dollars(-person.held)} under. The family pot covers it.`
                  : `${person.name || 'They'} ${person.name ? 'has' : 'have'} ${dollars(person.held)} of their own. It goes to the family pot.`}
              </p>
            ) : null}
            <button
              type="button"
              className="danger-button full"
              disabled={busy}
              onClick={() => {
                setRemoving(false);
                onRemove();
              }}
            >
              Remove {person.name || 'them'}
            </button>
            <button type="button" className="full ghost-strong" data-autofocus onClick={() => setRemoving(false)}>
              Keep {person.name || 'them'}
            </button>
          </div>
        </div>
        </Layer>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------

/**
 * Recording money.
 *
 * The direction is a choice rather than a minus sign somebody has to remember
 * to type, because "-20" and "20" look nearly identical on a phone and mean
 * opposite things to a family's money.
 */
function Record({
  people,
  busy,
  onRecord,
}: {
  people: Person[];
  busy: boolean;
  onRecord: (entry: { amount: string; reason: string; people: string[] }) => Promise<boolean>;
}) {
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);

  const ready = looksLikeAmount(amount) && reason.trim() !== '';

  function toggle(id: string): void {
    setChosen((current) =>
      current.includes(id) ? current.filter((one) => one !== id) : [...current, id],
    );
  }

  const cents = looksLikeAmount(amount) ? Math.round(parseFloat(normaliseAmount(amount) || '0') * 100) : 0;
  const symbol = currentUnit().type === 'currency' ? dollars(0).replace(/[\d.,\s\u00a0]/g, '') : '';
  const whoWords = (() => {
    const names = chosen.map((id) => people.find((p) => p.id === id)?.name || 'Somebody');
    if (names.length === 0) return 'the family';
    if (names.length === 1) return names[0]!;
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.length} people`;
  })();
  // THE BUTTON SAYS WHAT WILL HAPPEN, in money and names, and when it
  // cannot happen yet it says what is missing instead of greying out.
  const label = busy
    ? 'Saving…'
    : !looksLikeAmount(amount)
      ? 'Add an amount first'
      : reason.trim() === ''
        ? 'Say what it’s for'
        : direction === 'in'
          ? `Add ${dollars(cents)} for ${whoWords}`
          : `Record ${dollars(cents)} spent by ${whoWords}`;

  return (
    <div className="record money-entry">
      <div className="segmented" role="group" aria-label="Money in or out">
        <button
          type="button"
          className={direction === 'in' ? 'on' : ''}
          aria-pressed={direction === 'in'}
          onClick={() => setDirection('in')}
        >
          {inLabel()}
        </button>
        <button
          type="button"
          className={direction === 'out' ? 'on' : ''}
          aria-pressed={direction === 'out'}
          onClick={() => setDirection('out')}
        >
          Spent
        </button>
      </div>

      <label className={`amount-big ${direction}`}>
        <span className="visually-hidden">{howMuch()}</span>
        <span className="amount-sign" aria-hidden="true">
          {direction === 'in' ? '+' : '\u2212'}
          {symbol}
        </span>
        <input
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          inputMode={amountInputMode()}
          autoComplete="off"
          placeholder={amountPlaceholder()}
          size={Math.max(4, amount.length + 1)}
        />
      </label>

      <div className="quick-amounts" role="group" aria-label="Quick amounts">
        {[1, 2, 5, 10].map((n) => {
          const typed = asTyped(n * 100);
          return (
            <button
              type="button"
              key={n}
              className={normaliseAmount(amount) === normaliseAmount(typed) ? 'quick-amount on' : 'quick-amount'}
              onClick={() => setAmount(typed)}
            >
              {shortAmount(n * 100)}
            </button>
          );
        })}
      </div>

      {people.length ? (
        <div className="who-picker" role="group" aria-label="Who is this for?">
          {people.map((person) => {
            const on = chosen.includes(person.id);
            return (
              <button
                type="button"
                key={person.id}
                className={on ? 'who on' : 'who'}
                aria-pressed={on}
                onClick={() => toggle(person.id)}
              >
                <span className="face">
                  <Character spec={person.avatar} size={60} />
                  {on ? <span className="who-check" aria-hidden="true">&#10003;</span> : null}
                </span>
                <span className="who-name">{person.name || 'Unnamed'}</span>
              </button>
            );
          })}
          <button
            type="button"
            className={chosen.length === 0 ? 'who on family' : 'who family'}
            aria-pressed={chosen.length === 0}
            onClick={() => setChosen([])}
          >
            <span className="face face-all">
              <FamilyMark size={60} />
            </span>
            <span className="who-name">Family pot</span>
          </button>
        </div>
      ) : null}

      <label className="field reason-field">
        <span className="visually-hidden">What for?</span>
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={120}
          autoComplete="off"
          placeholder="Cinema tickets"
          aria-label="What for?"
        />
        <small>
          Encrypted before it leaves this device. Choose nobody and it is the
          whole family&rsquo;s; choose several and it splits evenly to the cent.
        </small>
      </label>

      <button
        className={ready && !busy ? `primary full record-go ${direction}` : 'full record-go waiting'}
        aria-busy={busy}
        type="button"
        disabled={busy || !ready}
        onClick={() =>
          void onRecord({
            amount: signed(amount, direction),
            reason: reason.trim(),
            people: chosen,
          }).then((saved) => {
            if (saved) {
              setAmount('');
              setReason('');
              setChosen([]);
            }
          })
        }
      >
        {label}
      </button>
    </div>
  );
}

function AddPerson({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (name: string) => Promise<boolean>;
}) {
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button type="button" className="full add-person" onClick={() => setOpen(true)}>
        + Add someone
      </button>
    );
  }

  return (
    <div className="record">
      <label className="field">
        <span>Their name</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={40}
          autoComplete="off"
          placeholder="Their name"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <small>Encrypted on this device before it is stored. The server never sees it.</small>
      </label>
      <div className="actions">
        <button aria-busy={busy}
          className="primary"
          type="button"
          disabled={busy || !name.trim()}
          onClick={() =>
            void onAdd(name.trim()).then((saved) => {
              if (saved) {
                setName('');
                setOpen(false);
              }
            })
          }
        >
          {busy ? 'Saving…' : 'Add them'}
        </button>
        <button type="button" disabled={busy} onClick={() => { setOpen(false); setName(''); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Putting the valley up on this screen -- which is how a family gets a wall
 * display: a parent's own account, signed in on the tablet in the kitchen.
 *
 * The PIN is what stands between that screen and the account behind it (the
 * sun asks for it). With no PIN there is nothing to ask for, so the way out is
 * open to whoever taps it. That is said BEFORE the valley goes up, with the
 * way to fix it, rather than discovered by a child.
 */
function ShowTheValley({ onShow, onSetPin }: { onShow: () => void; onSetPin: () => void }) {
  const [warning, setWarning] = useState(false);
  const account = currentAccount();
  const protectedByPin = Boolean(account && hasPin(account.sub));

  return (
    <section className="section">
      <h2>Show the valley</h2>
      <p className="quiet">
        The whole screen, kept up to date as anything changes on any device,
        with no way back to your account except your PIN. This is how to make
        a wall display: sign in on the tablet or spare laptop, then tap this.
      </p>
      {warning ? (
        <div className="danger-box">
          <p>
            <strong>You have no PIN on this device.</strong> Without one, anyone
            who taps the sun or moon gets straight into your account.
          </p>
          <div className="actions">
            <button type="button" className="primary" onClick={onSetPin}>
              Set a PIN first
            </button>
            <button type="button" onClick={onShow}>
              Show it anyway
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="full" onClick={() => (protectedByPin ? onShow() : setWarning(true))}>
          Show the valley on this screen
        </button>
      )}
    </section>
  );
}
